import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, MenuItem, Select, FormControl,
  InputLabel, Chip, SelectChangeEvent,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import api from '../../api/client';

interface Agency {
  id: number; name: string; abbreviation: string; agency_type: string;
  agency_type_id: number | null; is_active: boolean;
  case_number_mode: string; case_number_format: string;
}

interface AgencyTypeOption { id: number; code: string; name: string; is_active: boolean; }

export default function AgenciesPage() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Agency | null>(null);
  const [agencyTypes, setAgencyTypes] = useState<AgencyTypeOption[]>([]);
  const [form, setForm] = useState({ name: '', abbreviation: '', agency_type: 'law', agency_type_id: null as number | null, case_number_mode: 'every_call', case_number_format: '{AGENCY}-{YYYY}-{SEQ6}' });

  const load = () => api.get('/agencies').then((r) => setAgencies(r.data));
  useEffect(() => {
    load();
    api.get('/agency-types').then((r) => setAgencyTypes(r.data));
  }, []);

  const handleOpen = (agency?: Agency) => {
    if (agency) {
      setEditing(agency);
      setForm({ name: agency.name, abbreviation: agency.abbreviation, agency_type: agency.agency_type, agency_type_id: agency.agency_type_id, case_number_mode: agency.case_number_mode || 'every_call', case_number_format: agency.case_number_format || '{AGENCY}-{YYYY}-{SEQ6}' });
    } else {
      setEditing(null);
      setForm({ name: '', abbreviation: '', agency_type: 'law', agency_type_id: null, case_number_mode: 'every_call', case_number_format: '{AGENCY}-{YYYY}-{SEQ6}' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/agencies/${editing.id}`, form);
    } else {
      await api.post('/agencies', form);
    }
    setOpen(false);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Agencies</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Agency</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Abbreviation</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Case # Mode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {agencies.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.abbreviation}</TableCell>
                <TableCell>{a.agency_type}</TableCell>
                <TableCell>
                  <Chip size="small" label={a.case_number_mode === 'every_call' ? 'Every Call' : a.case_number_mode === 'on_request' ? 'On Request' : 'Never'} />
                </TableCell>
                <TableCell><Chip size="small" label={a.is_active ? 'Active' : 'Inactive'} color={a.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell><IconButton size="small" onClick={() => handleOpen(a)}><Edit fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Agency' : 'New Agency'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth label="Abbreviation" margin="normal" value={form.abbreviation} onChange={(e) => setForm({ ...form, abbreviation: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={form.agency_type_id !== null ? String(form.agency_type_id) : ''}
              label="Type"
              onChange={(e: SelectChangeEvent) => {
                const id = parseInt(e.target.value);
                const at = agencyTypes.find((t) => t.id === id);
                setForm({ ...form, agency_type_id: id, agency_type: at?.code || form.agency_type });
              }}
            >
              {agencyTypes.filter((t) => t.is_active).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Case Number Assignment</InputLabel>
            <Select value={form.case_number_mode} label="Case Number Assignment" onChange={(e: SelectChangeEvent) => setForm({ ...form, case_number_mode: e.target.value })}>
              <MenuItem value="every_call">Every Call (auto-assign)</MenuItem>
              <MenuItem value="on_request">On Request (manual)</MenuItem>
              <MenuItem value="never">Never</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Case Number Format" margin="normal"
            value={form.case_number_format}
            onChange={(e) => setForm({ ...form, case_number_format: e.target.value })}
            helperText="Tokens: {AGENCY} {YYYY} {YY} {MM} {DD} {SEQ} {SEQ3} {SEQ4} {SEQ5} {SEQ6}"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
